import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSelector } from 'react-redux'
import { getSelectedBp } from 'features/bpDetails/ducks/selectors'
import styled from 'styled-components'
import { Tabs, Tab } from '@mui/material'
import { styled as muiStyled } from '@mui/material/styles'
import { getFileViewedLogs } from 'services/fileService'
import AccessHistoryTab, { useAccessHistory } from 'features/documentAccess/AccessHistoryTab'
import CloseIcon from '@mui/icons-material/Close'
import VisibilityIcon from '@mui/icons-material/Visibility'
import DownloadIcon from '@mui/icons-material/Download'
import LoginIcon from '@mui/icons-material/Login'
import BlockIcon from '@mui/icons-material/Block'
import { formatDateWithTime } from 'utils/date'
import { USER_ROLES } from 'utils/userRoles'
import { isEmpty } from 'ramda'
import { hideEmail } from 'utils/user'

const STATUS_HELP = {
  'Not opened': 'This person has not opened the document.',
  'Signed in, not opened yet': 'This person has signed in since it was shared, but has not opened the document yet.',
  Viewed: 'This person has opened the document.',
  Downloaded: 'This person has downloaded a copy of the document.'
}

const VIEW_TABS = [
  { key: 'current', label: 'Has access now' },
  { key: 'historic', label: 'Used to have access' },
  { key: 'external', label: 'People outside your organisation' },
  { key: 'history', label: 'Access history' }
]

const ViewedStatusModal = ({ file, children, externalOpen, onExternalClose, canSeeHistory }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [internalOpen, setInternalOpen] = useState(false)
  const [tab, setTab] = useState('current')

  const open = externalOpen !== undefined ? externalOpen : internalOpen
  const selectedBp = useSelector(getSelectedBp)
  const showHistory = canSeeHistory ?? !!selectedBp?.isOwner
  const history = useAccessHistory(file?.id, !!open && showHistory)
  const historyHidden = !showHistory || history.forbidden
  const tabs = historyHidden ? VIEW_TABS.filter(t => t.key !== 'history') : VIEW_TABS

  const currentUsers = data?.current || []
  const historicUsers = data?.previous || []
  const externalUsers = data?.external || []

  const handleOpen = (e) => {
    e.stopPropagation()
    if (externalOpen === undefined) {
      setInternalOpen(true)
    }
  }

  const handleClose = () => {
    if (onExternalClose) {
      onExternalClose()
    } else {
      setInternalOpen(false)
    }
  }

  useEffect(() => {
    if (historyHidden && tab === 'history') setTab('current')
  }, [historyHidden, tab])

  useEffect(() => {
    if (open) {
      setLoading(true)
      getFileViewedLogs({ fileId: file.id })
        .then(resp => {
          setData(resp.data.data)
          setLoading(false)
        })
        .catch(() => {
          setData([])
          setLoading(false)
        })
    }
  }, [file, open])

  const getStatusLabel = (status, type) => {
    if (type === 'external') {
      switch (status) {
        case 'view':
          return 'Viewed'
        case 'download':
          return 'Downloaded'
        default:
          return 'Not opened'
      }
    }
    switch (status) {
      case 'viewed':
        return 'Viewed'
      case 'downloaded':
        return 'Downloaded'
      case 'logged_in':
        return 'Signed in, not opened yet'
      case 'not_logged_in':
        return 'Not opened'
      default:
        return 'Not opened'
    }
  }

  const getStatusHelp = (status, type) => STATUS_HELP[getStatusLabel(status, type)] || ''

  const getStatusIcon = (status, type) => {
    if (type === 'external') {
      switch (status) {
        case 'view':
          return <VisibilityIcon />
        case 'download':
          return <DownloadIcon />
        default:
          return <BlockIcon />
      }
    }
    switch (status) {
      case 'viewed':
        return <VisibilityIcon />
      case 'downloaded':
        return <DownloadIcon />
      case 'logged_in':
        return <LoginIcon />
      default:
        return <BlockIcon />
    }
  }

  const renderCards = (usersData, type) => {
    if (isEmpty(usersData)) {
      return (
        <EmptyState>
          <EmptyIcon>📭</EmptyIcon>
          <EmptyText>No users to display</EmptyText>
        </EmptyState>
      )
    }

    return (
      <CardGrid>
        {usersData.map(user => (
          <UserCard key={user.id || user.email} external={type === 'external'}>
            {/* Mobile Layout */}
            <MobileCardContent>
              <MobileCardHeader>
                <StatusIconWrapper status={user.status?.value || user.status} type={type}>
                  {getStatusIcon(user.status?.value || user.status, type)}
                </StatusIconWrapper>
                <MobileUserInfo>
                  <MobileUserName>
                    {type === 'external' ? hideEmail(user.email) : `${user.firstName} ${user.lastName}`}
                  </MobileUserName>
                  {type !== 'external' && user.email && <UserEmail>{hideEmail(user.email)}</UserEmail>}
                  <MobileUserRole>
                    {type === 'external'
                      ? 'External User'
                      : (USER_ROLES[user.relationType] || 'User')}
                  </MobileUserRole>
                </MobileUserInfo>
                <MobileStatusBadge status={user.status?.value || user.status} type={type} title={getStatusHelp(user.status?.value || user.status, type)}>
                  {getStatusLabel(user.status?.value || user.status, type)}
                </MobileStatusBadge>
              </MobileCardHeader>
              <MobileCardStats>
                <MobileStatRow>
                  <MobileStatLabel>Last opened</MobileStatLabel>
                  <MobileStatValue>{user.viewedAt ? formatDateWithTime(user.viewedAt) : 'Never opened'}</MobileStatValue>
                </MobileStatRow>
                <MobileStatRow>
                  <MobileStatLabel>Views</MobileStatLabel>
                  <MobileStatValue>{user?.viewsCount || '0'}</MobileStatValue>
                </MobileStatRow>
                <MobileStatRow>
                  <MobileStatLabel>Downloaded</MobileStatLabel>
                  <MobileStatValue>{user.downloadedAt ? formatDateWithTime(user.downloadedAt) : 'Never'}</MobileStatValue>
                </MobileStatRow>
              </MobileCardStats>
            </MobileCardContent>

            {/* Desktop Layout */}
            <DesktopCardContent>
              <RoleBadge className="role-badge">
                {type === 'external'
                  ? 'External'
                  : (USER_ROLES[user.relationType] || 'User')}
              </RoleBadge>
              <UserHeader>
                <StatusChip status={user.status?.value || user.status} title={getStatusHelp(user.status?.value || user.status, type)}>
                  {getStatusLabel(user.status?.value || user.status, type)}
                </StatusChip>
              </UserHeader>
              <UserName>
                {type === 'external' ? hideEmail(user.email) : `${user.firstName} ${user.lastName}`}
              </UserName>
              {type !== 'external' && user.email && <UserEmail>{hideEmail(user.email)}</UserEmail>}
              <UserMeta>
                <div>Last opened: {user.viewedAt ? formatDateWithTime(user.viewedAt) : 'Never opened'}</div>
                <div>Views: {user?.viewsCount || '0'}</div>
                <div>Downloaded: {user.downloadedAt ? formatDateWithTime(user.downloadedAt) : 'Never'}</div>
              </UserMeta>
            </DesktopCardContent>
          </UserCard>
        ))}
      </CardGrid>
    )
  }

  const renderTabContent = () => {
    switch (tab) {
      case 'current':
        return renderCards(currentUsers, 'current')
      case 'historic':
        return renderCards(historicUsers, 'historic')
      case 'external':
        return renderCards(externalUsers, 'external')
      case 'history':
        return <AccessHistoryTab history={history} />
      default:
        return null
    }
  }

  const renderLegend = () => {
    if (tab === 'current' || tab === 'historic') {
      return (
        <LegendInline>
          <LegendBlock>
            <LegendRow><LegendDot color="#9ca3af"/> Not opened</LegendRow>
            <LegendRow><LegendDot color="#0f6f78"/> Signed in, not opened yet</LegendRow>
            <LegendRow><LegendDot color="#10b981"/> Viewed</LegendRow>
            <LegendRow><LegendDot color="#475569"/> Downloaded</LegendRow>
          </LegendBlock>
        </LegendInline>
      )
    }

    if (tab === 'external') {
      return (
        <LegendInline>
          <LegendBlock>
            <LegendRow><LegendDot color="#10b981"/> Viewed</LegendRow>
            <LegendRow><LegendDot color="#475569"/> Downloaded</LegendRow>
          </LegendBlock>
        </LegendInline>
      )
    }

    return null
  }

  const tabCountReady = (tabName) => (tabName === 'history' ? !history.loading : !loading)

  const getTabCount = (tabName) => {
    switch (tabName) {
      case 'current':
        return currentUsers.length
      case 'historic':
        return historicUsers.length
      case 'external':
        return externalUsers.length
      case 'history':
        return history.total ?? history.rows.length
      default:
        return 0
    }
  }

  const modalContent = open ? (
    <Backdrop onClick={handleClose}>
      <ModalContainer onClick={e => e.stopPropagation()}>
        <MobileHandle />

        <DesktopHeader>
          <TitleWrapper>
            <Title>Views and downloads</Title>
            <FileNameLine>{file.displayName || file.name}</FileNameLine>
            <Subtitle>
              To change who can see this document, use the Access tab.
            </Subtitle>
          </TitleWrapper>
          <DesktopCloseButton type='button' onClick={handleClose} aria-label='Close'>
            <CloseIcon aria-hidden='true' />
          </DesktopCloseButton>
        </DesktopHeader>

        <MobileHeader>
          <MobileHeaderContent>
            <MobileTitle>Views and downloads</MobileTitle>
            <MobileFileName>{file.displayName || file.name}</MobileFileName>
          </MobileHeaderContent>
          <MobileCloseButton type='button' onClick={handleClose} aria-label='Close'>
            <CloseIcon aria-hidden='true' />
          </MobileCloseButton>
        </MobileHeader>

        <DesktopTabsWrapper>
          <StyledTabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
            {tabs.map(t => (
              <StyledTab key={t.key} value={t.key} label={tabCountReady(t.key) ? `${t.label} (${getTabCount(t.key)})` : t.label} />
            ))}
          </StyledTabs>
        </DesktopTabsWrapper>

        <MobileTabsWrapper>
          <MobileTabsScroll>
            {tabs.map(t => (
              <MobileTab key={t.key} type='button' active={tab === t.key} aria-pressed={tab === t.key} onClick={() => setTab(t.key)}>
                <span>{t.label}</span>
                {tabCountReady(t.key) && <MobileTabCount active={tab === t.key}>{getTabCount(t.key)}</MobileTabCount>}
              </MobileTab>
            ))}
          </MobileTabsScroll>
        </MobileTabsWrapper>

        <ModalContent>
          {loading && tab !== 'history' ? (
            <SkeletonGrid aria-busy='true' aria-label='Loading who has opened this document'>
              {[0, 1, 2, 3].map(i => (
                <SkeletonCard key={i} data-testid='viewed-skeleton'>
                  <SkeletonLine $w='40%' />
                  <SkeletonLine $w='70%' />
                  <SkeletonLine $w='55%' />
                </SkeletonCard>
              ))}
            </SkeletonGrid>
          ) : (
            <>
              {renderTabContent()}
              {tab !== 'history' && renderLegend()}
            </>
          )}
        </ModalContent>
      </ModalContainer>
    </Backdrop>
  ) : null

  return (
    <Wrapper>
      {children && (
        <TriggerWrapper onClick={handleOpen}>
          {children}
        </TriggerWrapper>
      )}
      {modalContent && typeof document !== 'undefined'
        ? createPortal(modalContent, document.body)
        : modalContent}
    </Wrapper>
  )
}

export default ViewedStatusModal

const Wrapper = styled.div``

const TriggerWrapper = styled.div`
  cursor: pointer;
`

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, .4);
  z-index: 999999;
  animation: fadeIn 0.2s ease;

  @media (max-width: 768px) {
    align-items: flex-end;
    background: rgba(0, 0, 0, 0.5);
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`

const ModalContainer = styled.div`
  width: 90vw;
  max-width: 1100px;
  max-height: 85vh;
  overflow-y: auto;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);

  @media (max-width: 768px) {
    width: 100%;
    height: 85vh;
    max-height: 85vh;
    border-radius: 20px 20px 0 0;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
    animation: slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    flex-direction: column;
  }

  @keyframes slideUp {
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
  }
`

const MobileHandle = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
    width: 36px;
    height: 4px;
    background: #d1d5db;
    border-radius: 2px;
    margin: 10px auto 6px;
    flex-shrink: 0;
  }
`

const DesktopHeader = styled.div`
  padding: 20px 24px;
  border-bottom: none;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  @media (max-width: 768px) {
    display: none;
  }
`

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const Title = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: ${({theme}) => theme.colors.haiti};
  word-break: break-word;
`

const DesktopCloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: none;
  color: #475569;
  cursor: pointer;

  &:hover { background: #f1f5f9; }
  &:focus-visible { outline: 2px solid #33becc; outline-offset: 2px; }
`

const SkeletonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
`

const SkeletonCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
`

const SkeletonLine = styled.span`
  display: block;
  width: ${({ $w }) => $w};
  height: 12px;
  border-radius: 6px;
  background: #f1f5f9;
  animation: viewedPulse 1.4s ease-in-out infinite;

  @keyframes viewedPulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.55; }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

const FileNameLine = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #334155;
  margin-top: 2px;
  overflow-wrap: anywhere;
`

const Subtitle = styled.div`
  font-size: 13px;
  color: ${({theme}) => theme.colors.darkGrey};
`


const MobileHeader = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-bottom: 1px solid #f3f4f6;
    flex-shrink: 0;
  }
`

const MobileHeaderContent = styled.div`
  flex: 1;
  min-width: 0;
`

const MobileTitle = styled.div`
  font-size: 17px;
  font-weight: 700;
  color: #1f2937;
`

const MobileFileName = styled.div`
  font-size: 12px;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 2px;
`

const MobileCloseButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  margin-left: 12px;

  svg {
    font-size: 20px;
    color: #6b7280;
  }

  &:active {
    background: #e5e7eb;
    transform: scale(0.95);
  }
`

const DesktopTabsWrapper = styled.div`
  padding: 0 24px;
  border-bottom: none;

  @media (max-width: 768px) {
    display: none;
  }
`

const StyledTabs = muiStyled(Tabs)({
  minHeight: 48,
  '& .MuiTabs-indicator': {
    display: 'none'
  },
  '& .MuiTabs-scrollButtons': {
    width: 'auto',
  }
})

const StyledTab = muiStyled(Tab)({
  textTransform: 'uppercase',
  fontWeight: 600,
  fontSize: 13,
  minHeight: 40,
  borderRadius: '20px',
  padding: '6px 18px',
  marginRight: 8,
  color: '#6b7280',
  backgroundColor: 'transparent',
  transition: 'all 0.2s ease',
  whiteSpace: 'nowrap',

  '&:hover': {
    backgroundColor: '#f3f4f6',
    color: '#34bdcc',
  },
  '&.Mui-selected': {
    backgroundColor: '#34bdcc',
    color: '#fff'
  }
})

const MobileTabsWrapper = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
    padding: 12px 16px;
    border-bottom: 1px solid #f3f4f6;
    flex-shrink: 0;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`

const MobileTabsScroll = styled.div`
  display: flex;
  gap: 8px;
`

const MobileTab = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 40px;
  padding: 10px 16px;
  border-radius: 20px;
  border: none;
  background: ${({ active }) => active ? '#33becc' : '#f3f4f6'};
  color: ${({ active }) => active ? '#0c1229' : '#475569'};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;

  &:active {
    transform: scale(0.98);
  }
`

const MobileTabCount = styled.span`
  background: ${({ active }) => active ? 'rgba(255,255,255,0.25)' : '#e5e7eb'};
  color: ${({ active }) => active ? '#fff' : '#6b7280'};
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
`

const ModalContent = styled.div`
  padding: 20px 24px 30px;

  @media (max-width: 768px) {
    padding: 16px;
    padding-bottom: calc(16px + env(safe-area-inset-bottom));
    flex: 1;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
`

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`

const UserCard = styled.div`
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  border: ${({external, theme}) =>
  external ? `1px solid ${theme.colors.secondary}` : `1px solid ${theme.colors.lightGrey}`};
  position: relative;
  transition: all 0.25s ease;
  overflow: hidden;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border-color: ${({theme}) => theme.colors.secondary};
  }

  @media (max-width: 768px) {
    border-radius: 12px;

    &:hover {
      transform: none;
    }
  }

  &:hover .role-badge {
    opacity: 1;
    transform: translateY(0);
  }
`

const DesktopCardContent = styled.div`
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (max-width: 768px) {
    display: none;
  }
`

const RoleBadge = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background: ${({theme}) => theme.colors.secondary};
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 6px;
  max-width: calc(100% - 20px);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: 0;
  transform: translateY(-5px);
  transition: all 0.25s ease;
  z-index: 2;
`

const UserHeader = styled.div`
  display: flex;
  align-items: center;
`

const UserEmail = styled.div`
  margin-top: 2px;
  font-size: 14px;
  color: #4b5563;
  overflow-wrap: anywhere;
`

const UserName = styled.div`
  font-weight: 600;
  font-size: 14px;
`

const UserMeta = styled.div`
  font-size: 12px;
  color: ${({theme}) => theme.colors.darkGrey};
  opacity: 0.85;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const StatusChip = styled.div`
  display: inline-block;
  font-size: 13px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 12px;
  background: ${({status}) =>
  status === 'viewed' || status === 'view' ? '#d1fae5' :
    status === 'logged_in' ? '#e6f7f9' :
      status === 'download' || status === 'downloaded' ? '#f1f5f9' : '#f3f4f6'};
  color: ${({status}) =>
  status === 'viewed' || status === 'view' ? '#065f46' :
    status === 'logged_in' ? '#0f6f78' :
      status === 'download' || status === 'downloaded' ? '#475569' : '#374151'};
`

const MobileCardContent = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
  }
`

const MobileCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  background: #fafbfc;
  border-bottom: 1px solid #f3f4f6;
`

const StatusIconWrapper = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${({ status, type }) => {
  if (type === 'external') {
    if (status === 'view') return '#d1fae5';
    if (status === 'download') return '#fef3c7';
    return '#f3f4f6';
  }
  if (status === 'viewed') return '#d1fae5';
  if (status === 'downloaded') return '#f1f5f9';
  if (status === 'logged_in') return '#e6f7f9';
  return '#f3f4f6';
}};

  svg {
    font-size: 20px;
    color: ${({ status, type }) => {
  if (type === 'external') {
    if (status === 'view') return '#059669';
    if (status === 'download') return '#d97706';
    return '#9ca3af';
  }
  if (status === 'viewed') return '#059669';
  if (status === 'downloaded') return '#475569';
  if (status === 'logged_in') return '#0f6f78';
  return '#9ca3af';
}};
  }
`

const MobileUserInfo = styled.div`
  flex: 1;
  min-width: 0;
`

const MobileUserName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const MobileUserRole = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
`

const MobileStatusBadge = styled.div`
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
  background: ${({ status, type }) => {
  if (type === 'external') {
    if (status === 'view') return '#d1fae5';
    if (status === 'download') return '#fef3c7';
    return '#f3f4f6';
  }
  if (status === 'viewed') return '#d1fae5';
  if (status === 'downloaded') return '#f1f5f9';
  if (status === 'logged_in') return '#e6f7f9';
  return '#f3f4f6';
}};
  color: ${({ status, type }) => {
  if (type === 'external') {
    if (status === 'view') return '#059669';
    if (status === 'download') return '#d97706';
    return '#6b7280';
  }
  if (status === 'viewed') return '#059669';
  if (status === 'downloaded') return '#475569';
  if (status === 'logged_in') return '#0f6f78';
  return '#6b7280';
}};
`

const MobileCardStats = styled.div`
  padding: 0;
`

const MobileStatRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  border-bottom: 1px solid #f9fafb;

  &:last-child {
    border-bottom: none;
  }
`

const MobileStatLabel = styled.div`
  font-size: 13px;
  color: #6b7280;
`

const MobileStatValue = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
`

// ============ LEGEND ============
const LegendInline = styled.div`
  margin: 20px 0;
  padding: 14px 20px;
  border-radius: 8px;
  background: ${({theme}) => theme.colors.backgroundColor};
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 40px;
  font-size: 13px;

  @media (max-width: 768px) {
    gap: 16px;
    padding: 14px 16px;
    margin: 16px 0 0 0;
    border-radius: 10px;
    background: #f9fafb;
  }
`

const LegendBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 16px;
  }
`

const LegendRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${({theme}) => theme.colors.darkGrey};
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`

const LegendDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({color}) => color};

  @media (max-width: 768px) {
    width: 8px;
    height: 8px;
  }
`

// ============ LOADING & EMPTY ============


const EmptyState = styled.div`
  padding: 20px;
  text-align: center;
  font-size: 13px;
  color: ${({theme}) => theme.colors.darkGrey};
  opacity: 0.7;

  @media (max-width: 768px) {
    padding: 40px 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }
`

const EmptyIcon = styled.div`
  font-size: 40px;
  display: none;

  @media (max-width: 768px) {
    display: block;
  }
`

const EmptyText = styled.div`
  @media (max-width: 768px) {
    font-size: 14px;
    color: #6b7280;
  }
`
